import { Button } from '@/components/Button';
import { LineSeparator } from '@/components/LineSeparator';
import { Modal } from '@/components/Modal';
import { PasswordInput } from '@/components/PasswordInput';
import { Text } from '@/components/Text';
import { useAuthStore } from '@/stores/useAuthStore';
import { UpdateAuthUserPasswordForm } from '@/types/user';
import { handleError } from '@/utils/handleError';
import { Form, Formik, FormikHelpers } from 'formik';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

export const AuthUserSettings = () => {
  const { updateAuthUserPassword } = useAuthStore();
  const { deleteAuthUser } = useAuthStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useTranslation();

  const initialValues = {
    current_password: '',
    new_password: '',
    confirm_password: '',
  };

  const validationSchema = yup.object({
    current_password: yup
      .string()
      .required(
        t('userSettings:security.password.error.current-password-required'),
      ),
    new_password: yup
      .string()
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z\d\s:]).{6,}$/,
        t('userSettings:security.password.error.new-password-format'),
      )
      .required(
        t('userSettings:security.password.error.new-password-required'),
      ),
    confirm_password: yup
      .string()
      .oneOf(
        [yup.ref('new_password')],
        t('userSettings:security.password.error.passwords-not-match'),
      )
      .required(
        t('userSettings:security.password.error.confirm-password-required'),
      ),
  });

  const handleDeleteUser = async () => {
    try {
      const { message } = await deleteAuthUser();
      toast.success(message);
    } catch (error) {
      handleError(error);
    } finally {
      setIsModalOpen(false);
    }
  };
  const handleOpen = () => {
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const handleUpdateUserPassword = async (
    values: UpdateAuthUserPasswordForm,
    {
      setSubmitting,
      setErrors,
      resetForm,
    }: FormikHelpers<UpdateAuthUserPasswordForm>,
  ) => {
    try {
      const { message } = await updateAuthUserPassword(values);
      toast.success(message);
      resetForm();
    } catch (error) {
      handleError(error, setErrors);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Section mot de passe */}
      <Text className="text-center font-medium text-sky-800 md:text-left">
        {t('userSettings:security.password.title')}
      </Text>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleUpdateUserPassword}
      >
        {({ isSubmitting, errors, touched }) => (
          <Form className="mt-4 grid w-full grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
            <div className="space-y-1">
              <PasswordInput
                label={t('userSettings:security.password.label.current')}
                name="current_password"
                placeholder={t(
                  'userSettings:security.password.placeholder.current',
                )}
                error={!!touched.current_password && !!errors.current_password}
              />
            </div>
            <div className="space-y-1">
              <PasswordInput
                label={t('userSettings:security.password.label.new')}
                name="new_password"
                placeholder={t(
                  'userSettings:security.password.placeholder.new',
                )}
                error={!!touched.new_password && !!errors.new_password}
              />
            </div>
            <div className="col-span-1 space-y-1 sm:col-span-2">
              <PasswordInput
                label={t('userSettings:security.password.label.confirm')}
                name="confirm_password"
                placeholder={t(
                  'userSettings:security.password.placeholder.confirm',
                )}
                error={!!touched.confirm_password && !!errors.confirm_password}
              />
            </div>
            <div className="mt-4 sm:col-span-2">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full lg:w-auto"
              >
                {t('userSettings:security.password.update')}
              </Button>
            </div>
          </Form>
        )}
      </Formik>

      <LineSeparator className="my-8" />

      {/* Section suppression de compte */}
      <Text className="text-center font-medium text-sky-800 md:text-left">
        {t('userSettings:delete.title')}
      </Text>
      <div className="mt-4 w-full lg:w-auto">
        <Button className="w-full bg-danger lg:w-auto" onClick={handleOpen}>
          {t('userSettings:delete.submit')}
        </Button>
      </div>

      {/* Modal de confirmation */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleClose}
        className="w-auto"
        title={t('userSettings:delete.validation.title')}
      >
        <p className="mb-4 text-gray-600">
          {t('userSettings:delete.validation.description')}
        </p>
        <div className="mt-6 flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-end sm:space-x-3 sm:space-y-0">
          <Button className="w-full sm:w-auto" onClick={handleClose}>
            {t('common:cancel')}
          </Button>
          <Button
            className="w-full bg-danger sm:w-auto"
            onClick={handleDeleteUser}
          >
            {t('common:confirm')}
          </Button>
        </div>
      </Modal>
    </>
  );
};
